/**
 * My requests — prototype `myreq` (docs/prototype.html 3514, final): the Sales / Marketing home. Stat strip,
 * Updates, Your requests (with the status help), "+ Tell Ops what you need". Data: GET /me/requests (own
 * requests, updates, unread count, typical_turnaround); the drawer is the shared Request panel (`?request=`).
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { INTERNAL_REQUEST_STATUS_LABEL, INTERNAL_REQUEST_TYPE_LABEL, type InternalRequest } from '@amplispot/contracts';
import { call } from '../../api';
import { firstName, todayIso } from '../../lib/format';
import { IR_TONE, dateNeedLabel, isOpenIR, needLower, notifText, slaText, turnaroundText, useMyRequests } from '../../lib/requests';
import { useSession } from '../../lib/session';
import { useWorkWrite } from '../../lib/work';
import { Lbl, PageInfo } from '../../shell/Help';
import { Empty, ErrorState, Loading } from '../../ui/kit';
import { InternalRequestModal, NotYetModal, useConfirmDone } from './ItemModals';

const STATUS_HELP: [keyof typeof IR_TONE, string][] = [
  ['waiting_triage', 'Ops has your request. Within 24 hours someone reads it and either picks it up, asks you a question, or says why they cannot. After 24 hours with no reply it is flagged to the Ops associate director.'],
  ['in_progress', 'An owner is assigned and working. You see the date they expect to finish, and any delay with a reason.'],
  ['needs_details', 'Ops asked you a question. The clock is paused until you answer in the conversation.'],
  ['waiting_confirm', 'Ops says it is done. Confirm, or tell them what is missing and it goes back to them.'],
  ['done', 'You confirmed it.'],
];

export function MyRequests() {
  const { readOnly } = useSession();
  const q = useMyRequests();
  const [, setSp] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const [help, setHelp] = useState(false);
  const [notYet, setNotYet] = useState<InternalRequest | null>(null);
  const confirm = useConfirmDone();
  const markRead = useWorkWrite((ids: string[]) => call('notifications.read', { body: { ids } }));
  const today = todayIso();

  const data = q.data?.data ?? [];
  const notes = q.data?.notifications ?? [];
  const unread = q.data?.unread_count ?? 0;
  // The tiles count ALL the caller's requests on the server (`data` is the latest 200 only).
  const open = q.data?.stats.open ?? 0;
  const waiting = q.data?.stats.waiting_on_you ?? 0;
  const done = q.data?.stats.done_this_quarter ?? 0;
  // null while fewer than 5 of the team's requests closed in 90 days — too few to be typical, so no tile.
  const turnaround = q.data?.typical_turnaround ?? null;

  const openReq = (id: string) => {
    const ids = notes.filter((n) => n.request_id === id && !n.read).map((n) => n.id);
    if (ids.length && !readOnly) markRead.mutate(ids);
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set('request', id);
      return n;
    });
  };

  return (
    <>
      <div className="ph">
        <div>
          <h1>
            My requests
            <PageInfo helpKey="my-requests" />
          </h1>
          <p>Tell Ops what you need, in your own words. You will see where it stands, and what happens next.</p>
        </div>
        {!readOnly ? (
          <div className="r">
            <button className="btn pri" onClick={() => setAdding(true)}>
              + Tell Ops what you need
            </button>
          </div>
        ) : null}
      </div>

      {q.isLoading ? (
        <div className="panel">
          <Loading />
        </div>
      ) : q.error ? (
        <div className="panel">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </div>
      ) : (
        <>
          <div className="stats">
            <div className="stat">
              <div className="k">Open</div>
              <div className="v">{open}</div>
            </div>
            <div className={`stat${waiting ? ' amber' : ''}`}>
              <div className="k">Waiting on you</div>
              <div className="v">{waiting}</div>
              <div className="s">a reply or a confirmation</div>
            </div>
            <div className="stat green">
              <div className="k">Done</div>
              <div className="v">{done}</div>
              <div className="s">this quarter</div>
            </div>
            {turnaround ? (
              <div className="stat" title={`Median of ${turnaround.sample} of your team's requests closed in the last 90 days`}>
                <div className="k">
                  <Lbl>Typical turnaround</Lbl>
                </div>
                <div className="v">{turnaroundText(turnaround.median_days)}</div>
                <div className="s">for requests like these</div>
              </div>
            ) : null}
          </div>

          {notes.length ? (
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="hd">
                <h3><Lbl>Updates</Lbl></h3>
                <div className="r">
                  {unread ? <span className="chip hot">{unread} new</span> : null}
                  {/* Always offered, as in the prototype; with nothing unread it does nothing. */}
                  {!readOnly ? (
                    <button
                      className="btn sm ghost"
                      onClick={() => {
                        const ids = notes.filter((n) => !n.read).map((n) => n.id);
                        if (ids.length) markRead.mutate(ids);
                      }}
                      disabled={markRead.isPending}
                    >
                      Mark all read
                    </button>
                  ) : null}
                </div>
              </div>
              {notes.slice(0, 5).map((n) => (
                <div key={n.id} className="li click" role="button" tabIndex={0} onClick={() => n.request_id && openReq(n.request_id)} onKeyDown={(e) => e.key === 'Enter' && n.request_id && openReq(n.request_id)}>
                  <div className="b">
                    <div className="t" style={n.read ? { fontWeight: 500 } : undefined}>
                      {n.read ? null : <span className="dot hot" style={{ display: 'inline-block', marginRight: 6 }} />}
                      {n.text}
                    </div>
                    <div className="m">
                      <span>{notifText(n.at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="panel">
            <div className="hd">
              <h3><Lbl>Your requests</Lbl></h3>
              <div className="r">
                <button className="btn sm ghost" onClick={() => setHelp((h) => !h)} aria-expanded={help}>
                  What do the statuses mean?
                </button>
              </div>
            </div>
            {help ? (
              <div className="bd" style={{ borderBottom: '1px solid var(--line)', display: 'grid', gap: 10 }}>
                {STATUS_HELP.map(([k, s]) => (
                  <div key={k}>
                    <span className={`chip ${IR_TONE[k]}`}>{INTERNAL_REQUEST_STATUS_LABEL[k as InternalRequest['status']]}</span>
                    <div className="sub" style={{ marginTop: 3 }}>
                      {s}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {data.length ? (
              data.map((r) => {
                const live = isOpenIR(r.status);
                const later = !!r.expected_by && r.expected_by > r.needed_by;
                const c = r.clock.value;
                return (
                  <div key={r.id} className="li click" role="button" tabIndex={0} onClick={() => openReq(r.id)} onKeyDown={(e) => e.key === 'Enter' && openReq(r.id)}>
                    <div className="b">
                      <div className="t">{r.text}</div>
                      <div className="m">
                        <span>{INTERNAL_REQUEST_TYPE_LABEL[r.type].label}</span>
                        <span>{r.owner ? `With ${firstName(r.owner.name)}` : 'Ops has not picked this up yet'}</span>
                        <span>Needed {needLower(dateNeedLabel(r.needed_by, today))}</span>
                        {r.expected_by && live ? (
                          <span style={later ? { color: 'var(--cold)', fontWeight: 600 } : undefined}>Expected {needLower(dateNeedLabel(r.expected_by, today))}</span>
                        ) : null}
                        {r.status === 'needs_details' ? <span style={{ color: 'var(--warm)', fontWeight: 600 }}>Ops needs a reply from you</span> : null}
                      </div>
                    </div>
                    {r.status === 'waiting_confirm' && !readOnly ? (
                      <>
                        <button
                          className="btn sm good"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirm.confirm(r.id);
                          }}
                          disabled={confirm.busy}
                        >
                          Confirm done
                        </button>
                        <button
                          className="btn sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNotYet(r);
                          }}
                        >
                          Not quite
                        </button>
                      </>
                    ) : null}
                    <span className={`chip ${IR_TONE[r.status]}`}>{INTERNAL_REQUEST_STATUS_LABEL[r.status]}</span>
                    {live && r.status !== 'needs_details' ? (
                      <span className={`chip ${c.is_breached ? 'cold' : ''}`}>{r.acked_at ? 'Acknowledged' : c.hours_to_ack != null ? slaText(c.hours_to_ack) : 'Acknowledged'}</span>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <Empty title="Nothing yet">Use the button above to send your first request.</Empty>
            )}
          </div>
        </>
      )}
      {adding ? <InternalRequestModal onClose={() => setAdding(false)} /> : null}
      {notYet ? <NotYetModal r={notYet} onClose={() => setNotYet(null)} /> : null}
    </>
  );
}
